import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Bell, Loader2, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";
import { publicApi } from "../lib/api";
import { trackEvent } from "../lib/analytics";

const CAT_ACCENT = ["bg-orange/10 text-orange", "bg-teal/10 text-teal", "bg-sky-100 text-sky-600", "bg-navy/5 text-navy", "bg-purple-100 text-purple-600"];

export default function KeyTaxDates() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", marketing_consent: true });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [hp, setHp] = useState("");

  useEffect(() => { publicApi.taxDates().then(setCfg).catch(() => {}); window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email) { setError("Please add your email."); return; }
    setBusy(true);
    try {
      await publicApi.taxDateReminder({ ...form, company_website: hp });
      trackEvent("sign_up", { method: "tax_date_reminders" }, "tax_dates_reminder");
      setDone(true);
    } catch (err) {
      setError(err?.response?.status === 429 ? "Too many attempts — please try again shortly." : "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div data-testid="key-tax-dates-page">
      <Seo title="Key UK Tax Dates & Deadlines" description="A clear calendar of key UK tax deadlines — Self Assessment, VAT, payroll, Corporation Tax and Making Tax Digital. Opt in for friendly reminders from CASS." />

      <section className="bg-navy-deep text-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6"><CalendarDays size={14} /> Resources</span>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">{cfg.title}</h1>
          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">{cfg.intro}</p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 space-y-10">
          {(cfg.categories || []).map((cat, ci) => (
            <div key={ci} data-testid={`tax-cat-${ci}`}>
              <div className="flex items-center gap-3 mb-5">
                <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${CAT_ACCENT[ci % CAT_ACCENT.length]}`}><CalendarDays size={20} /></span>
                <h2 className="font-display font-extrabold text-2xl text-navy">{cat.name}</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {(cat.dates || []).map((d, di) => (
                  <div key={di} data-testid={`tax-date-${ci}-${di}`} className="bg-white rounded-2xl border border-navy/5 shadow-sm p-5 flex gap-4">
                    <div className="shrink-0 text-center">
                      <div className="bg-navy text-white rounded-xl px-3 py-2 min-w-[92px]">
                        <p className="font-display font-extrabold text-sm leading-tight">{d.date}</p>
                      </div>
                    </div>
                    <div>
                      <p className="font-display font-bold text-navy leading-snug">{d.title}</p>
                      <p className="text-sm text-slate-500 mt-1 leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Topical internal links — the tax pages these deadlines belong to */}
      <section className="py-12 bg-white" data-testid="tax-dates-help-links">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display font-extrabold text-2xl text-navy mb-2">Need help meeting one of these?</h2>
          <p className="text-slate-600 mb-6 max-w-2xl">We handle the filing, the calculations and the reminders, so a deadline is never the first you hear of it.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { to: "/services/self-assessment", label: "Personal Tax & Self Assessment", note: "Tax returns for directors, sole traders and landlords" },
              { to: "/services/vat", label: "VAT Returns", note: "MTD-compliant VAT prepared and submitted for you" },
              { to: "/making-tax-digital-income-tax", label: "MTD for Income Tax", note: "Check if it applies to you, and see who files it" },
            ].map((l) => (
              <Link key={l.to} to={l.to} data-testid={`tax-help-link-${l.to.split("/").pop()}`}
                className="group bg-slate-50 hover:bg-white border border-navy/5 hover:border-orange/40 rounded-2xl p-5 transition-colors">
                <p className="font-display font-bold text-navy leading-snug group-hover:text-orange transition-colors">{l.label}</p>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{l.note}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
        <section className="py-14 md:py-20 bg-white" data-testid="tax-reminder-section">
          <div className="max-w-2xl mx-auto px-6">
            <div className="bg-navy-deep text-white rounded-3xl p-8 md:p-10 text-center">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-orange/20 text-orange mb-5"><Bell size={26} /></span>
              <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-2">{cfg.reminder_heading}</h2>
              <p className="text-slate-300 mb-6">{cfg.reminder_body}</p>
              {done ? (
                <div data-testid="tax-reminder-success" className="inline-flex items-center gap-2 text-teal font-semibold"><CheckCircle2 size={20} /> You're on the list — we'll nudge you before the key dates.</div>
              ) : (
                <form onSubmit={submit} className="space-y-3 text-left max-w-md mx-auto" data-testid="tax-reminder-form">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="tax-reminder-name" className="w-full rounded-xl px-4 py-3 text-navy focus:outline-none focus:ring-2 focus:ring-orange" />
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" data-testid="tax-reminder-email" className="w-full rounded-xl px-4 py-3 text-navy focus:outline-none focus:ring-2 focus:ring-orange" />
                  </div>
                  <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" />
                  {error && <p data-testid="tax-reminder-error" className="text-sm text-orange-300">{error}</p>}
                  <button disabled={busy} data-testid="tax-reminder-submit" className="w-full inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors">
                    {busy ? <Loader2 size={18} className="animate-spin" /> : <Bell size={18} />} {busy ? "Signing you up…" : "Send me reminders"}
                  </button>
                  <p className="text-[11px] text-slate-400 text-center">We'll only use your email for deadline reminders and occasional tips. Unsubscribe any time.</p>
                </form>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
